import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface PolicyRow {
  readonly policyId: string;
  readonly policyCode: string;
  readonly policyName: string;
  readonly policyCategory: string;
  readonly status: string;
}

const CATEGORIES = [
  '', 'INFORMATION_SECURITY', 'CYBER_SECURITY', 'DATA_PROTECTION', 'PRIVACY', 'ACCESS_CONTROL',
  'NETWORK_SECURITY', 'INCIDENT_RESPONSE', 'BUSINESS_CONTINUITY', 'THIRD_PARTY', 'ACCEPTABLE_USE', 'COMPLIANCE',
];
const STATUSES = ['', 'DRAFT', 'UNDER_REVIEW', 'APPROVED', 'PUBLISHED', 'DEPRECATED', 'RETIRED'];

/**
 * GR L30.10 — Policy Attestation (`/security/policies/attestation`). The user's
 * attestation surface over the `security_policy` register: published policies
 * the user can acknowledge (attest to having read). Filterable by category and
 * status.
 */
export function GrPolicyAttestationPage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [rows, setRows] = useState<PolicyRow[]>([]);
  const [policyCategory, setPolicyCategory] = useState('');
  const [status, setStatus] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [refresh, setRefresh] = useState(0);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (policyCategory) params.set('policyCategory', policyCategory);
    if (status) params.set('status', status);
    const qs = params.toString();
    apiRequest<{ data: PolicyRow[] }>(`/v1/security/policies${qs ? `?${qs}` : ''}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, policyCategory, status, refresh]);

  async function attest(id: string): Promise<void> {
    try {
      await apiRequest(`/v1/security/policies/${id}/acknowledge`, { token, method: 'POST', body: {} });
      setNotice(t('gr30.attestation.acknowledged'));      setError(null);
      setRefresh((n) => n + 1);
    } catch (e) {
      setError((e as Error).message);
    }
  }

  return (
    <main aria-label="security-attestation">
      <h1>{t('gr30.attestation.title')}</h1>
      <div className="filters">
        <label>
          {t('gr30.attestation.labelCategory')}
          <select aria-label="filter-category" value={policyCategory} onChange={(e) => setPolicyCategory(e.target.value)}>
            {CATEGORIES.map((c) => <option key={c || 'all'} value={c}>{c || t('gr30.attestation.all')}</option>)}
          </select>
        </label>
        <label>
          {t('gr30.attestation.labelStatus')}
          <select aria-label="filter-status" value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUSES.map((s) => <option key={s || 'all'} value={s}>{s || t('gr30.attestation.all')}</option>)}
          </select>
        </label>
      </div>
      {error && <p role="alert">{error}</p>}
      {notice && <p aria-label="notice" role="status">{notice}</p>}
      {loading ? <p>{t('gr30.attestation.loading')}</p> : (
        <>
          <p aria-label="policy-count">{t('gr30.attestation.count', { count: String(rows.length) })}</p>
          {rows.length === 0 ? <p aria-label="empty">{t('gr30.attestation.empty')}</p> : (
            <table aria-label="policy-table">
              <thead><tr><th>{t('gr30.attestation.colCode')}</th><th>{t('gr30.attestation.colName')}</th><th>{t('gr30.attestation.colCategory')}</th><th>{t('gr30.attestation.colStatus')}</th><th></th></tr></thead>
              <tbody>
                {rows.map((p) => (
                  <tr key={p.policyId}>
                    <td>{p.policyCode}</td><td>{p.policyName}</td><td>{p.policyCategory}</td><td>{p.status}</td>
                    <td>
                      <button type="button" aria-label={`attest-${p.policyId}`} onClick={() => attest(p.policyId)}>
                        {t('gr30.attestation.acknowledge')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </main>
  );
}
